/**
 * Direct project Start / Finish (D-3/A3) — the crew's two-button flow on a
 * PROJECT, mirroring `useTransitionTask`: field-time stamped at tap, an
 * idempotency key, and the offline queue so the tap that matters most (Finish
 * on a dead signal) is never lost. Plus the project's typed status timeline.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ProjectStatusEventsResponseSchema,
  type CrewTodayResponse,
  type Project,
  type ProjectStatusEventsResponse,
  type ProjectStatusTransitionInput,
} from '@vari-crew/schemas';
import { apiCall } from '@/lib/api';
import { enqueueMutation, isNetworkError, newIdempotencyKey } from '@/lib/offlineQueue';
import type { QueuedTransition } from './tasks';

export type ProjectTransitionResult = Project | QueuedTransition;

/** A project transition accepted into the offline queue instead of sent. */
export function isQueuedProjectResult(r: ProjectTransitionResult): r is QueuedTransition {
  return typeof r === 'object' && r !== null && 'queued' in r && r.queued === true;
}

export function useTransitionProject(projectId: string) {
  const qc = useQueryClient();
  return useMutation<ProjectTransitionResult, Error, ProjectStatusTransitionInput>({
    mutationFn: async (body) => {
      // Field time at tap, not at send (ADR-0014).
      const payload = { ...body, occurred_at: body.occurred_at ?? new Date().toISOString() };
      const key = newIdempotencyKey();
      try {
        return await apiCall<Project>(`/projects/${projectId}/transition`, {
          method: 'POST',
          body: payload,
          headers: { 'Idempotency-Key': key },
        });
      } catch (err) {
        if (!isNetworkError(err)) throw err;
        await enqueueMutation({
          path: `/projects/${projectId}/transition`,
          method: 'POST',
          body: payload,
          idempotencyKey: key,
          label: `Project update · ${payload.to.replace(/_/g, ' ')}`,
        });
        return { queued: true };
      }
    },
    onSuccess: (updated, vars) => {
      if (isQueuedProjectResult(updated)) {
        // Offline: advance the crew's local truth on Today so the card reflects
        // the tap; the server catches up on replay.
        qc.setQueriesData<CrewTodayResponse>({ queryKey: ['crew-today'] }, (old) =>
          old
            ? {
                ...old,
                projects: old.projects.map((p) =>
                  p.project_id === projectId ? { ...p, status: vars.to } : p,
                ),
              }
            : old,
        );
        return;
      }
      qc.setQueryData(['projects', 'detail', projectId], updated);
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['project-status-events', projectId] });
      qc.invalidateQueries({ queryKey: ['crew-today'] });
      qc.invalidateQueries({ queryKey: ['crew-schedule'] });
      qc.invalidateQueries({ queryKey: ['dispatch'] });
      qc.invalidateQueries({ queryKey: ['public-status'] });
    },
  });
}

export function useProjectStatusEvents(projectId: string | undefined) {
  return useQuery<ProjectStatusEventsResponse>({
    queryKey: ['project-status-events', projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const raw = await apiCall(`/projects/${projectId}/status-events`);
      return ProjectStatusEventsResponseSchema.parse(raw);
    },
  });
}
